<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useEvaluationPerformanceStore} from "@/stores/Evaluation/EvalautionPerformance";
import {computed, onMounted, ref, watch} from "vue";
import axios from "axios";
import {useSecurityStore} from "@/stores/Security";
import useSharedSelectData from "@/use/SharedSelectData";
import WCodeGeneration from "@/components/WCodeGeneration.vue";
import {useEvaluationFileStore} from "@/stores/Evaluation/EvaluationFile";

export default {
    components: {WCodeGeneration},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {
            evaluationEvaluatorTypes,
            evaluationCycles,
            quarterlies,
            months,
            halfYearlies
        } = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route, $moment} = useTools();
        const evaluationFileStore = useEvaluationFileStore();
        const evaluationPerformanceStore = useEvaluationPerformanceStore();
        const securityStore = useSecurityStore();

        const evaluationPerformance = ref({
            closed: false,
            done: false,
            concernedNumber: 0.0,
            completedNumber: 0.0,
            toleratedNumber: 0.0,
            cycle: null,
            cycleYear: $moment().year(),
            steps: [],
            filesId: [],
        });

        const filesId = computed(() => evaluationPerformance.value.filesId);

        watch(() => filesId.value, () => {
            if (filesId.value && filesId.value.length > 0 && props.addAction) {
                let fileId = filesId.value[0];
                let index = evaluationFileStore.evaluationFiles.findIndex(e => e.id === fileId);
                if (index > -1) {
                    let file = evaluationFileStore.evaluationFiles[index];
                    file.steps
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .forEach(step => {
                            evaluationPerformance.value.steps.push({
                                phaseId: step.phaseId,
                                done: false,
                                launched: false,
                                current: false,
                                concernedNumber: 0.0,
                                completedNumber: 0.0,
                                toleratedNumber: 0.0,
                            })
                        })
                }
            }
        })

        function addStep() {
            evaluationPerformance.value.steps.push({
                done: false,
                launched: false,
                current: false,
                concernedNumber: 0.0,
                completedNumber: 0.0,
                toleratedNumber: 0.0,
            });
        }

        function findById(id) {
            loading.value = true;
            axios.get(`/evaluationPerformances/${id}`).then((response) => {
                evaluationPerformance.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                buildOrder();
                axios.post('/evaluationPerformances', evaluationPerformance.value).then((response) => {
                    evaluationPerformanceStore.addEvaluationPerformance(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                buildOrder();
                axios.put('/evaluationPerformances', evaluationPerformance.value).then((response) => {
                    evaluationPerformanceStore.updateEvaluationPerformance(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function buildOrder() {
            evaluationPerformance.value.steps.forEach((step, index) => {
                step.order = index;
            });
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/evaluationPerformances/${evaluationPerformance.value.id}`).then(() => {
                    evaluationPerformanceStore.deleteEvaluationPerformance(evaluationPerformance.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            securityStore,
            evaluationPerformanceStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            evaluationPerformance,
            evaluationEvaluatorTypes,
            evaluationCycles,
            quarterlies,
            months,
            halfYearlies,
            addStep,
            save,
            update,
            remove,
            getBack,
        };
    }
}
</script>

<template>
    <div>
        <v-dialog
            v-model="dialog"
            max-width="900"
            persistent
            scrollable
        >
            <v-card>
                <w-dialog-toolbar
                    :view-action="viewAction"
                    :delete-action="deleteAction"
                    :add-action="addAction"
                    :update-action="updateAction"
                ></w-dialog-toolbar>
                <w-overlay
                    :overlay="overlay"
                    :loading="loading"
                    :delete-action="deleteAction"
                    @remove="remove"
                    @cancel="overlay = false"
                ></w-overlay>
                <v-card-text>
                    <v-container grid-list-md>
                        <v-form ref="form">
                            <w-fieldset :title="$t('common.details')">
                                <v-row dense>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.code')" required/>
                                        <w-code-generation
                                            v-model="evaluationPerformance.code"
                                            entity="EVALUATION_PERFORMANCE"
                                            :load="addAction"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        ></w-code-generation>
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.label')" required/>
                                        <w-text-field
                                            v-model="evaluationPerformance.name"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col :cols="evaluationPerformance.cycle === 'YEARLY' ? '6' : '12'">
                                        <w-label :label="$t('evaluation.cycle')" required/>
                                        <w-enum-select
                                            v-model="evaluationPerformance.cycle"
                                            :items="evaluationCycles"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6" v-if="evaluationPerformance.cycle === 'MONTHLY'">
                                        <w-label :label="$t('common.month')" required/>
                                        <w-enum-select
                                            v-model="evaluationPerformance.cycleMonth"
                                            :items="months"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6" v-if="evaluationPerformance.cycle === 'QUARTERLY'">
                                        <w-label :label="$t('common.QUARTERLY')" required/>
                                        <w-enum-select
                                            v-model="evaluationPerformance.cycleQuarterlyOrHalfYearly"
                                            :items="quarterlies"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6" v-if="evaluationPerformance.cycle === 'HALF_YEARLY'">
                                        <w-label :label="$t('common.HALF_YEARLY')" required/>
                                        <w-enum-select
                                            v-model="evaluationPerformance.cycleQuarterlyOrHalfYearly"
                                            :items="halfYearlies"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6"
                                           v-if="evaluationPerformance.cycle && evaluationPerformance.cycle !== 'SPECIFIC'">
                                        <w-label :label="$t('common.year')" required/>
                                        <w-text-field
                                            v-model="evaluationPerformance.cycleYear"
                                            type="number"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>

                                    <v-col cols="12" v-if="evaluationPerformance.cycle">
                                        <w-label :label="$t('module.evaluation_performance_files')" required/>
                                        <str-evaluation-file-autocomplete
                                            v-model="evaluationPerformance.filesId"
                                            type="EVALUATION_PERFORMANCE"
                                            :cycle="evaluationPerformance.cycle"
                                            multiple
                                            :readonly="readonly"
                                            :rules="[mxRules.listRequired]"
                                        />
                                    </v-col>
                                </v-row>

                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('common.period')">
                                <v-row dense>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.start')" required/>
                                        <w-date-picker
                                            v-model="evaluationPerformance.cycleStart"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="6">
                                        <w-label :label="$t('common.end')" required/>
                                        <w-date-picker
                                            v-model="evaluationPerformance.cycleEnd"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('evaluation.pay_gateway')">
                                <v-row dense>
                                    <v-col cols="12">
                                        <w-label :label="$t('common.month')" required/>
                                        <w-month-picker
                                            v-model="evaluationPerformance.gatewayMonth"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset
                                :title="$t('common.steps')"
                                :show-add-action="!readonly"
                                @add="addStep"
                            >
                                <v-row
                                    v-for="(step, index) in evaluationPerformance.steps"
                                    :key="`step-${index}`"
                                    dense
                                >
                                    <v-col cols="11">
                                        <w-label :label="$t('common.step')" required/>
                                        <str-evaluation-phase-autocomplete
                                            v-model="step.phaseId"
                                            type="EVALUATION_PERFORMANCE"
                                            :readonly="readonly"
                                            :rules="[mxRules.required]"
                                        />
                                    </v-col>
                                    <v-col cols="1">
                                        <w-btn-icon
                                            color="red"
                                            classe="mt-6"
                                            icon="mdi-trash-can"
                                            :press="readonly"
                                            @press="evaluationPerformance.steps.splice(index, 1)"
                                        />
                                    </v-col>
                                </v-row>
                            </w-fieldset>
                            <br/>
                            <w-fieldset :title="$t('common.comment')">
                                <w-simple-text-area
                                    v-model="evaluationPerformance.notes"
                                    :readonly="readonly"
                                />
                            </w-fieldset>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        :show="addAction"
                        :loading="loading"
                        @press="save"
                    />
                    <w-btn-update
                        :show="updateAction"
                        :loading="loading"
                        @press="update"
                    />
                    <w-btn-delete
                        :show="deleteAction"
                        @press="overlay = true"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
        <router-view/>
    </div>
</template>